/**
 * @fileoverview The season review's picture: a season on one page, with the Grow mark.
 *
 * Drawn with the plant share's own pieces (shareFrames): its three sizes and
 * the edges a story's app covers, its faces, the Grow mark, the handle, the
 * cream card and the photographs read once a session. A few of the
 * gardener's photos, the ones they chose, across the top; under them, on
 * cream, the mark, the season's name and days, its numbers (plants, beds,
 * photos), its firsts (planting, harvest), and the gardener's own line.
 *
 * What it never prints: an address, a town, a postcode, a bed's name (beds
 * are often named for where they are), or a note. Plants and beds are
 * counted, not listed.
 *
 * The words sit at the foot (above what a story's app covers) and the
 * photographs take everything above them. When the words need more room
 * than the frame has, they shrink together (down to SCALE_FLOOR) before the
 * photographs give up more than a third of the frame.
 *
 * Where: SeasonReviewPage (buildSeasonFrame).
 * API: the chosen photos' files, through shareFrames loadSubjectPhoto.
 * Settings: SHARE_SIZES, SHARE_SAFE, SHARE_JPEG_QUALITY (the plant share's).
 * Words (en/es): none of its own (the words come in already said);
 * plants.share.canvasUnavailable and plants.share.renderFailed on failure.
 *
 * @module seasonFrame
 */

import { plantSubject } from '@/shared/api/subject';
import type { LinkKey } from '@/shared/lib/socialLinks';
import { t } from '@/shared/i18n/i18nSetup';
import { SHARE_JPEG_QUALITY } from '@/config/tunables/plantList';
import {
  BODY, SERIF, SHARE_SAFE, SHARE_SIZES, drawCover, drawGrowMark, drawHandleAt, ensureFonts, groundColours,
  loadSubjectPhoto, wrap, type ShareSize,
} from '@/features/plant-list/lib/shareFrames';

/** One of the season's numbers: "12" over "plants". */
export interface SeasonStat {
  /** The number, as printed. */
  value: string;
  /** What it counts. */
  label: string;
}

/** Everything the season's picture prints, already in words. */
export interface SeasonFrameInput {
  /** The season's year, for the file's name. */
  year: number;
  /** The picture's size. */
  size: ShareSize;
  /** The heading: "2026 in the garden". */
  title: string;
  /** The season's days: "Growing season · Apr 6 – Oct 29". */
  spanLine: string;
  /** Its numbers, up to three, in a row. */
  stats: SeasonStat[];
  /** Its firsts, a line each: "First planting · Sep 16". */
  lines: string[];
  /** The gardener's own line, or ''. */
  words: string;
  /** The photos across the top, in order: each plant's photo by its plant. */
  photos: Array<{ plantObjId: number; photoId: number }>;
  /** Their handle, and where it lives; null to leave it off. */
  handle: { text: string; place: LinkKey } | null;
  /** Whether the Grow mark heads the words (false for an account the Grow settings leave it off). */
  showMark: boolean;
}

/** The card's cream, as the plant share's card layout has it. */
const CREAM = '#FBF8F1';
/** Strong ink on cream: the heading, the numbers, the gardener's words. */
const INK = '#3D3935';
/** Soft ink on cream: the days, the labels, the firsts. */
const SOFT = 'rgba(61,57,53,0.78)';
/** The Grow mark's green on cream. */
const MARK = '#2D5A27';
/** The smallest the words shrink to, against the frame's own unit, before the photographs give way. */
const SCALE_FLOOR = 0.7;
/** The share of the frame the photographs keep at least: more with photos, a band without. */
const PHOTO_FLOOR = { photos: 0.34, none: 0.16 };

/** The words, measured at one scale, before anything is drawn. */
interface Plan {
  /** The scale the words were measured at. */
  s: number;
  /** The heading, wrapped. */
  titleLines: string[];
  /** The gardener's line, wrapped. */
  wordLines: string[];
  /** From the block's top to its last baseline. */
  height: number;
}

/**
 * Measures the block of words at a scale.
 *
 * @param ctx - The frame's canvas.
 * @param input - What is printed.
 * @param inner - The width the lines may take.
 * @param U - The frame's unit (its width over 1080).
 * @param s - The scale.
 * @param tall - Whether the frame is tall (a story or a pin), which gives the words a third line.
 */
function plan(ctx: CanvasRenderingContext2D, input: SeasonFrameInput, inner: number, U: number, s: number, tall: boolean): Plan {
  const k = U * s;
  ctx.font = `700 ${Math.round(84 * k)}px ${SERIF}`;
  const titleLines = wrap(ctx, input.title, inner, 2);
  ctx.font = `400 ${Math.round(48 * k)}px ${BODY}`;
  const wordLines = input.words.trim() ? wrap(ctx, input.words, inner, tall ? 3 : 2) : [];
  let height = input.showMark ? Math.round(40 * k) + Math.round(34 * k) : 0;
  height += Math.round(84 * k) + Math.round(96 * k) * (titleLines.length - 1);
  height += Math.round(56 * k);
  if (input.stats.length) height += Math.round(40 * k) + Math.round(130 * k);
  height += input.lines.length * Math.round(56 * k) + (input.lines.length ? Math.round(12 * k) : 0);
  if (wordLines.length) height += Math.round(40 * k) + Math.round(62 * k) * wordLines.length;
  return { s, titleLines, wordLines, height };
}

/**
 * The photographs across the top: one fills the band; two side by side (or
 * stacked in a tall band); three as one large and two small; four in a
 * grid. Cream lines between them, as between prints.
 *
 * @param ctx - The frame's canvas.
 * @param images - The photographs, decoded, in order.
 * @param W - The band's width (the frame's).
 * @param h - The band's height.
 * @param U - The frame's unit.
 */
function drawMosaic(ctx: CanvasRenderingContext2D, images: ImageBitmap[], W: number, h: number, U: number): void {
  const gap = Math.round(8 * U);
  const tall = h > W * 0.9;
  /** The cells, as [x, y, w, h]. */
  let cells: Array<[number, number, number, number]> = [];
  const halfW = Math.floor((W - gap) / 2);
  const halfH = Math.floor((h - gap) / 2);
  if (images.length === 1) cells = [[0, 0, W, h]];
  else if (images.length === 2) {
    cells = tall
      ? [[0, 0, W, halfH], [0, halfH + gap, W, h - halfH - gap]]
      : [[0, 0, halfW, h], [halfW + gap, 0, W - halfW - gap, h]];
  } else if (images.length === 3) {
    if (tall) {
      cells = [[0, 0, W, halfH], [0, halfH + gap, halfW, h - halfH - gap], [halfW + gap, halfH + gap, W - halfW - gap, h - halfH - gap]];
    } else {
      const big = Math.floor((W - gap) * 0.6);
      cells = [[0, 0, big, h], [big + gap, 0, W - big - gap, halfH], [big + gap, halfH + gap, W - big - gap, h - halfH - gap]];
    }
  } else {
    cells = [
      [0, 0, halfW, halfH], [halfW + gap, 0, W - halfW - gap, halfH],
      [0, halfH + gap, halfW, h - halfH - gap], [halfW + gap, halfH + gap, W - halfW - gap, h - halfH - gap],
    ];
  }
  images.slice(0, cells.length).forEach((image, index) => {
    const [x, y, w, ch] = cells[index];
    drawCover(ctx, image, x, y, w, ch);
  });
}

/**
 * The band in place of photographs, for a picture with none: the garden
 * green, with the Grow mark large in its middle (none on an account without
 * the mark), in the ground's own ink.
 *
 * @param ctx - The frame's canvas.
 * @param W - The band's width.
 * @param h - The band's height.
 * @param U - The frame's unit.
 * @param showMark - Whether the account carries the mark.
 */
function drawPlainBand(ctx: CanvasRenderingContext2D, W: number, h: number, U: number, showMark: boolean): void {
  const ground = groundColours('green');
  ctx.fillStyle = ground.fill;
  ctx.fillRect(0, 0, W, h);
  if (!showMark) return;
  const k = U * 2.4;
  ctx.font = `600 ${Math.round(36 * k)}px ${SERIF}`;
  const width = Math.round(34 * k) + Math.round(8 * k) + ctx.measureText('Grow').width;
  drawGrowMark(ctx, (W - width) / 2, h / 2 + Math.round(14 * k), k, ground.ink);
}

/**
 * The block of words on cream, from its top edge down.
 *
 * @param ctx - The frame's canvas.
 * @param input - What is printed.
 * @param p - The block, measured.
 * @param x - The block's left edge.
 * @param top - Its top edge.
 * @param inner - Its width.
 * @param U - The frame's unit.
 */
function drawWords(ctx: CanvasRenderingContext2D, input: SeasonFrameInput, p: Plan, x: number, top: number, inner: number, U: number): void {
  const k = U * p.s;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  let cursor = top;
  if (input.showMark) {
    cursor += Math.round(40 * k);
    drawGrowMark(ctx, x, cursor, k, MARK);
    cursor += Math.round(34 * k);
  }
  ctx.font = `700 ${Math.round(84 * k)}px ${SERIF}`;
  ctx.fillStyle = INK;
  cursor += Math.round(84 * k);
  p.titleLines.forEach((line, index) => ctx.fillText(line, x, cursor + Math.round(96 * k) * index));
  cursor += Math.round(96 * k) * (p.titleLines.length - 1);

  cursor += Math.round(56 * k);
  ctx.font = `400 ${Math.round(40 * k)}px ${BODY}`;
  ctx.fillStyle = SOFT;
  ctx.fillText(input.spanLine, x, cursor, inner);

  if (input.stats.length) {
    cursor += Math.round(40 * k);
    const column = inner / input.stats.length;
    input.stats.forEach((stat, index) => {
      const left = x + column * index;
      ctx.font = `700 ${Math.round(80 * k)}px ${SERIF}`;
      ctx.fillStyle = INK;
      ctx.fillText(stat.value, left, cursor + Math.round(78 * k), column - Math.round(12 * k));
      ctx.font = `400 ${Math.round(36 * k)}px ${BODY}`;
      ctx.fillStyle = SOFT;
      ctx.fillText(stat.label, left, cursor + Math.round(126 * k), column - Math.round(12 * k));
    });
    cursor += Math.round(130 * k);
  }

  if (input.lines.length) {
    cursor += Math.round(12 * k);
    ctx.font = `400 ${Math.round(40 * k)}px ${BODY}`;
    ctx.fillStyle = INK;
    input.lines.forEach((line) => {
      cursor += Math.round(56 * k);
      ctx.fillText(line, x, cursor, inner);
    });
  }

  if (p.wordLines.length) {
    cursor += Math.round(40 * k);
    ctx.font = `400 ${Math.round(48 * k)}px ${BODY}`;
    ctx.fillStyle = INK;
    p.wordLines.forEach((line, index) => ctx.fillText(line, x, cursor + Math.round(62 * k) * (index + 1) - Math.round(14 * k)));
  }
}

/**
 * The season's picture, as a JPEG file.
 *
 * @param input - What it prints, and its size.
 * @returns The picture, named grow-season-<year>.jpg.
 */
export async function buildSeasonFrame(input: SeasonFrameInput): Promise<File> {
  await ensureFonts();
  const { w: W, h: H } = SHARE_SIZES[input.size];
  const U = W / 1080;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(t('plants.share.canvasUnavailable'));

  ctx.fillStyle = CREAM;
  ctx.fillRect(0, 0, W, H);

  const margin = Math.round(64 * U);
  const inner = W - margin * 2;
  const safeTop = Math.round(H * SHARE_SAFE[input.size].top);
  const safeBottom = Math.round(H * SHARE_SAFE[input.size].bottom);
  const tall = H / W >= 1.2;
  const hasPhotos = input.photos.length > 0;
  const floor = Math.round(H * (hasPhotos ? PHOTO_FLOOR.photos : PHOTO_FLOOR.none));
  const gapAbove = Math.round(44 * U);

  // Without photographs the band carries the mark large, so the words go without it.
  const words: SeasonFrameInput = hasPhotos ? input : { ...input, showMark: false };
  // The largest words that leave the photographs their floor.
  let p = plan(ctx, words, inner, U, 1, tall);
  while (p.s > SCALE_FLOOR && H - safeBottom - margin - p.height - gapAbove < floor) {
    p = plan(ctx, words, inner, U, Math.max(SCALE_FLOOR, p.s - 0.05), tall);
  }
  const top = H - safeBottom - margin - p.height;
  // The photographs take everything above the words, so nothing is left blank.
  const band = Math.max(floor, top - gapAbove);

  if (hasPhotos) {
    const images: ImageBitmap[] = [];
    try {
      for (const { plantObjId, photoId } of input.photos) images.push(await loadSubjectPhoto(plantSubject(plantObjId), photoId));
      drawMosaic(ctx, images, W, band, U);
    } finally {
      images.forEach((image) => image.close());
    }
  } else {
    drawPlainBand(ctx, W, band, U, input.showMark);
  }

  // The words sit at the foot, the photographs above; a short block leaves cream between them.
  drawWords(ctx, words, p, margin, Math.max(top, band + gapAbove), inner, U);
  // White over photographs; the green's own ink on the plain band.
  if (input.handle) drawHandleAt(ctx, input.handle, margin, safeTop, U * p.s * 1.25, hasPhotos ? undefined : groundColours('green').ink);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', SHARE_JPEG_QUALITY));
  if (!blob) throw new Error(t('plants.share.renderFailed'));
  return new File([blob], `grow-season-${input.year}.jpg`, { type: 'image/jpeg' });
}
